import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { CARD_ENTRY_EASING, prefersReducedMotion } from "./card-motion";

/* ------------------------------------------------------------------ *
 * A PART OF THE FELT LEAVING — the generic one-shot departure شلم's کف flies with (P1 Task 5: the
 * four to the حاکم, the four laid down to its pile). Its own module (P1 Task 6, a pure move out of
 * `ShelemTable.tsx`), beside `card-motion.ts`'s `ThrownCard` — the ARRIVAL it mirrors — so another
 * table (پاسور) can import it without touching either game's screen.
 * ------------------------------------------------------------------ */

/**
 * A part of the felt LEAVING for somewhere: it travels to `to` (a CSS transform) and fades as it
 * arrives, `delay` ms after the frame — WAAPI, guarded (a runtime without `.animate`, or a player who
 * asked for reduced motion, draws it at rest for the moment's length, as `ThrownCard` does), a
 * `backwards` fill only (it waits in place through its delay — never `both` / `forwards`), and its
 * own opacity set to 0 the moment it arrives, so it never snaps back before it is unmounted. Its
 * resting position is its caller's `style`; it carries no resting transform of its own.
 * `to` may be a function of the element, called the moment the departure starts (its layout effect,
 * the frame it flies in already laid out) — for a target that must be MEASURED on that frame, never
 * on the one before it (شلم's کف: the declaring frame swaps the panel and the felt resizes).
 */
export function Departing({
  to,
  delay,
  duration,
  style,
  children,
}: {
  to: string | ((el: HTMLElement) => string);
  delay: number;
  duration: number;
  style: CSSProperties;
  children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function" || prefersReducedMotion()) return undefined;
    const anim = el.animate(
      [
        { transform: "translate(0px, 0px)", opacity: 1 },
        { transform: typeof to === "function" ? to(el) : to, opacity: 0 },
      ],
      { duration, delay, easing: CARD_ENTRY_EASING, fill: "backwards" },
    );
    anim.onfinish = (): void => {
      el.style.opacity = "0";
      anim.cancel();
    };
    return () => anim.cancel();
    // MOUNT-ONCE: a departure plays exactly once, when its node appears (keyed by its moment).
  }, []);
  return (
    <span ref={ref} className="absolute" style={style}>
      {children}
    </span>
  );
}
